import {
  activeExclusiveOption,
  defaultFieldValues,
  type FieldValue,
  type FieldValues,
  withPrimaryKpi,
} from './fieldValues.js';
import type { WorkflowField } from './workflows.types.js';

function assign(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  const leaf = segments.pop();
  if (leaf === undefined) return;

  let node = target;
  for (const segment of segments) {
    const existing = node[segment];
    const child = typeof existing === 'object' && existing !== null ? existing : {};
    node[segment] = child;
    node = child as Record<string, unknown>;
  }
  node[leaf] = value;
}

function isSet(value: FieldValue | undefined): value is FieldValue {
  if (value === undefined || value === null) return false;
  return Array.isArray(value) ? value.length > 0 : value !== '';
}

function collect(
  fields: WorkflowField[],
  values: FieldValues,
  defaults: FieldValues,
  params: Record<string, unknown>,
): void {
  for (const field of fields) {
    if (field.kind === 'collapsible') {
      collect(field.fields, values, defaults, params);
      continue;
    }

    // Alternative spellings of one gate: submitting both is rejected by the API,
    // so only the option the toggle has active is sent — and it is always sent,
    // even at its prefilled value. Only one alternative can carry a model
    // default, so skipping the other as "unchanged" would silently drop it.
    if (field.kind === 'exclusiveGroup') {
      const active = activeExclusiveOption(field, values);
      const value = active === undefined ? undefined : (values[active.key] ?? active.default);
      if (active !== undefined && value !== undefined && value !== null) {
        assign(params, active.key, value);
      }
      continue;
    }

    const value = values[field.key];
    // A card choice may have no service-side default, so the picked card is always sent.
    if (!isSet(value) || (value === defaults[field.key] && field.kind !== 'presetCards')) continue;
    if (field.kind === 'select' || field.kind === 'segmented' || field.kind === 'multiselect') {
      const optionValue = (id: string | number): string | number =>
        field.options.find((option) => option.id === String(id))?.value ?? id;
      const mapped = Array.isArray(value) ? value.map(optionValue) : optionValue(value);
      assign(
        params,
        field.key,
        field.kind === 'select' && field.asList && !Array.isArray(mapped) ? [mapped] : mapped,
      );
    } else {
      assign(params, field.key, value);
    }
  }
}

function read(params: Record<string, unknown>, path: string): unknown {
  let node: unknown = params;
  for (const segment of path.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return node;
}

function isFieldValue(value: unknown): value is FieldValue {
  if (typeof value === 'string' || typeof value === 'number') return true;
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function creativeName(entry: unknown): string[] {
  if (typeof entry !== 'object' || entry === null) return [];
  const { name } = entry as { name?: unknown };
  return typeof name === 'string' && name !== '' ? [name] : [];
}

function restore(
  fields: WorkflowField[],
  params: Record<string, unknown>,
  values: FieldValues,
): void {
  for (const field of fields) {
    if (field.kind === 'collapsible') {
      restore(field.fields, params, values);
      continue;
    }

    if (field.kind === 'exclusiveGroup') {
      const sent = field.options.find((option) => read(params, option.key) !== undefined);
      if (sent !== undefined) values[field.key] = sent.key;
      restore(field.options, params, values);
      continue;
    }

    const value = read(params, field.key);
    // A run holds its creatives as objects; the field shows them by name.
    if (field.kind === 'upload') {
      if (Array.isArray(value)) values[field.key] = value.flatMap(creativeName);
      continue;
    }
    if (field.kind === 'select' && field.asList) {
      const [only] = Array.isArray(value) ? value : [value];
      if (typeof only === 'string' || typeof only === 'number') values[field.key] = String(only);
      continue;
    }
    if (
      field.kind === 'multiselect' &&
      Array.isArray(value) &&
      value.every((entry) => typeof entry === 'string' || typeof entry === 'number')
    ) {
      values[field.key] = value.map(String);
    } else if (isFieldValue(value)) values[field.key] = value;
  }
}

export function fieldValuesFromParams(
  fields: WorkflowField[],
  params: Record<string, unknown>,
): FieldValues {
  const values = defaultFieldValues(fields);
  restore(fields, params, values);
  return values;
}

// A step that already ran shows what it ran with, so only a fresh one is seeded.
export function openingValues(
  fields: WorkflowField[],
  ranParams: Record<string, unknown> | null,
  inherited: Record<string, unknown>,
  leadingKpi: string,
): FieldValues {
  if (ranParams !== null) return fieldValuesFromParams(fields, ranParams);
  // A parent ranked on one Primary KPI; the next step's KPIs multiselect opens on it.
  const { kpi, kpis } = inherited;
  const carried = typeof kpi === 'string' && kpi !== '' && kpis === undefined;
  const from = carried ? { ...inherited, kpis: [kpi] } : inherited;
  return withPrimaryKpi(fields, fieldValuesFromParams(fields, from), leadingKpi);
}

export function buildRunParams(
  fields: WorkflowField[],
  values: FieldValues,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  collect(fields, values, defaultFieldValues(fields), params);
  return params;
}
